import { useQuery } from '@apollo/client';
import { Col, Row, Tooltip } from 'antd';
import classNames from 'classnames';
import { get } from 'lodash-es';
import { FormattedMessage, useIntl } from 'react-intl';

import { USER_QUERY } from '../../../app/graphql/authQueries';
import { formatCodeValue } from '../../../common/utils/formatUtils';
import { extractGraphqlEntity } from '../../../common/utils/graphqlUtils';
import { FATypes } from '../../../components/adapters/fontAwesomeAdapters';
import IconButton from '../../../components/buttons/IconButton';
import { Spinner } from '../../../components/data/Spinner';
import { FormItemInputCurrency } from '../../../components/domainSpecific/currencyElements';
import FormList from '../../../components/forms/FormList';
import {
  FormItemFieldRegistration,
  FormItemInputText,
  FormItemInputTextArea,
} from '../../../components/forms/basicFormElements';
import { FormItemYesNo } from '../../../components/forms/checkable';
import DynamicFieldsForm from '../../../components/forms/dynamic/DynamicFieldsForm';
import {
  useDynamicFormSchemaDynamicFieldsDefinition,
  useDynamicFormSchemaFieldDefinition,
  useDynamicFormSchemaFieldExists,
} from '../../../components/forms/dynamic/dynamicFormSchema';
import { useDynamicFormValidationRules } from '../../../components/forms/dynamic/dynamicFormValidation';
import { useFormListCallbacks } from '../../../components/forms/formDecorators';
import {
  EnsureInitialFormListItem,
  mergeFormNames,
} from '../../../components/forms/formHelpers';
import { numericRule } from '../../../components/forms/formValidationRules';
import { useFormContext } from '../../../components/forms/forms';
import { FormItemSchemaDefinedSelect } from '../../../components/forms/selects';
import { ConfigurableCard } from '../../../components/layout/cardElements';
import {
  FlexCol,
  TwoLines,
  TwoLinesSeparator,
} from '../../../components/layout/layoutElements';
import { useLayoutElementsStyles } from '../../../components/layout/layoutElements.styles';
import { useResponsiveQueries } from '../../../components/responsive/responsiveQueries';
import { cssVariables } from '../../../styles/cssVariables';
import { pxToNumber } from '../../../utils/cssUtils';
import { DividerCol } from '../packageList/packageListCommon';
import { usePackageListCommonStyles } from '../packageList/packageListCommon.styles';
import { ServiceTypeField } from '../shipmentFormsCommon/shipmentServiceInformationFields';
import { useNewOrderFeatures } from '../shipmentModes';
import { NewShipmentEmailNotificationsButton } from './NewShipmentEmailNotifications';
import { NewShipmentBaseRow, NewShipmentForm } from './newShipmentElements';
import { useNewShipmentElementsStyles } from './newShipmentElements.styles';
import { useNewShipmentServiceInformationFormStyles } from './NewShipmentServiceInformationForm.styles';
import { useTooltipStyles } from '../../../styles/tooltip.styles';

const TopRowCol = ({ children, className, ...rest }) => {
  const { styles } = useNewShipmentServiceInformationFormStyles();
  return (
    <Col className={styles.newShipmentServiceInformationTopCol} {...rest}>
      {children}
    </Col>
  );
};

const SECOND_CARD_DYN_PROPS = {
  schemaName: 'serviceInformation.shipperSpecificInfo',
  namePrefix: 'shipperSpecificInfo',
  rowComponent: NewShipmentBaseRow,
  colComponent: TopRowCol,
};
export function ShipperSpecificCard({ namePrefix }) {
  const { styles: newShipmentElementsStyles } = useNewShipmentElementsStyles();
  const dynamicProps = {
    ...SECOND_CARD_DYN_PROPS,
    namePrefix: mergeFormNames(namePrefix, [SECOND_CARD_DYN_PROPS.namePrefix]),
  };
  const fields = useDynamicFormSchemaDynamicFieldsDefinition({
    schemaName: dynamicProps.schemaName,
  });

  return (
    fields.length > 0 && (
      <ConfigurableCard
        titleId="book.newShipment.shipperSpecificInformation"
        bodyComponent="div"
        bodyClassName={classNames(
          'NewShipmentCardBody',
          'ShipperSpecificCard',
          newShipmentElementsStyles.newShipmentCardBody,
          'spaces-vert-norm'
        )}
      >
        <DynamicFieldsForm {...dynamicProps} />
      </ConfigurableCard>
    )
  );
}

const BolNumber = ({ namePrefix }) => (
  <FormItemInputText
    name={mergeFormNames(namePrefix, 'bolNumber')}
    schemaName="serviceInformation.bolNumber"
    labelId="book.newShipment.label.hawbBol"
  />
);
export const IncotermsField = ({ namePrefix }) => (
  <FormItemSchemaDefinedSelect
    name={mergeFormNames(namePrefix, 'incoterms')}
    schemaName="serviceInformation.incoterms"
    labelId="book.newShipment.label.incoterms"
    allowFreeText
  />
);
const CostCenter = ({ namePrefix }) => (
  <FormItemSchemaDefinedSelect
    name={mergeFormNames(namePrefix, 'costCenter')}
    schemaName="serviceInformation.costCenter"
    labelId="book.newShipment.label.costCenter"
    allowFreeText
  />
);
const DeclaredValue = ({ namePrefix }) => (
  <FormItemInputCurrency
    name={mergeFormNames(namePrefix, 'declaredValue')}
    schemaName="serviceInformation.declaredValue"
    labelId="book.newShipment.label.declaredValue"
    rules={schemaRules => [...schemaRules, numericRule]}
  />
);
const CurrencyInput = ({ namePrefix }) => (
  <FormItemSchemaDefinedSelect
    name={mergeFormNames(namePrefix, 'currency')}
    schemaName="referenceInformation.currency"
    labelId="book.newShipment.label.currency"
    formItemComponentProps={{
      formatLabel: value =>
        value?.code ? `(${value?.code}) ${value?.text}` : value?.text,
    }}
  />
);
const AdditionalInsurance = ({ namePrefix }) => (
  <FormItemInputCurrency
    name={mergeFormNames(namePrefix, 'additionalInsurance')}
    schemaName="serviceInformation.additionalInsurance"
    labelId="book.newShipment.label.additionalInsurance"
    rules={schemaRules => [...schemaRules, numericRule]}
  />
);
function EoriNumber({ namePrefix }) {
  const { styles } = useTooltipStyles();
  return (
    <Tooltip
      title={
        <FormattedMessage id="book.newShipment.label.eoriNumber.tooltip" />
      }
      classNames={{ root: styles.sm }}
      placement="topLeft"
    >
      <div>
        <FormItemInputText
          name={mergeFormNames(namePrefix, 'eoriNumber')}
          schemaName="serviceInformation.eoriNumber"
          labelId="book.newShipment.label.eoriNumber"
        />
      </div>
    </Tooltip>
  );
}

function ReferenceNumbers({ namePrefix }) {
  const { formatMessage } = useIntl();
  const { styles } = useTooltipStyles();

  return (
    <Tooltip
      title={<FormattedMessage id="book.newShipment.label.refNo.placeholder" />}
      classNames={{ root: styles.sm }}
      placement="topLeft"
    >
      <div>
        <FormItemInputTextArea
          name={mergeFormNames(namePrefix, 'refNo')}
          formItemComponentProps={{
            placeholder: formatMessage({
              id: 'book.newShipment.label.refNo.placeholder',
            }),
            maxLength: 100,
            autoSize: {
              minRows: 1,
              maxRows: 4,
            },
          }}
        />
      </div>
    </Tooltip>
  );
}

function HsInfoItem({ fields, index, add, remove, move }) {
  const { styles } = useTooltipStyles();
  const { styles: commonStyles } = usePackageListCommonStyles();
  const { styles: layoutStyles } = useLayoutElementsStyles();
  const { values } = useFormContext();
  const { addAfter, removeThis } = useFormListCallbacks({
    fields,
    index,
    add,
    remove,
    move,
  });

  const currentValue = get(values, ['hsInfo', index]);

  return (
    <Row align="middle" gutter={pxToNumber(cssVariables.spaceNorm2)}>
      <Col>
        <FlexCol
          align="center"
          justify="space-around"
          className={commonStyles.pieceListControls}
        >
          <IconButton
            icon="trash-alt"
            onClick={removeThis}
            titleId="buttons.delete"
            data-subject="piece-list"
            data-action="delete"
          />
          <IconButton
            icon="clone"
            onClick={() => addAfter(currentValue)}
            titleId="buttons.copy"
            data-subject="piece-list"
            data-action="copy"
          />
          <IconButton
            icon="plus-square"
            onClick={addAfter}
            titleId="buttons.add"
            data-subject="piece-list"
            data-action="add-after"
          />
        </FlexCol>
      </Col>
      <Col
        className={classNames(
          layoutStyles.flex1,
          commonStyles.pieceListEnvelope
        )}
      >
        <NewShipmentBaseRow align="top">
          <Col style={{ width: `calc(50% - ${cssVariables.spaceNorm2})` }}>
            <Tooltip
              title={
                <FormattedMessage id="book.newShipment.label.hsCode.tooltip" />
              }
              classNames={{ root: styles.sm }}
              placement="topLeft"
            >
              <div>
                <FormItemInputText
                  name={[index, 'code']}
                  schemaName="serviceInformation.hsInfo.code"
                  labelId="book.newShipment.label.hsCode"
                />
              </div>
            </Tooltip>
          </Col>
          <Col className={layoutStyles.flex1}>
            <FormItemInputText
              name={[index, 'description']}
              schemaName="serviceInformation.hsInfo.description"
              labelId="book.newShipment.label.hsDescription"
            />
          </Col>
        </NewShipmentBaseRow>
      </Col>
    </Row>
  );
}
const HsInfo = ({ namePrefix }) => (
  <div className="spaces-vert-norm">
    <FormList name={mergeFormNames(namePrefix, 'hsInfo')}>
      {(fields, { add, remove, move }) => (
        <>
          <EnsureInitialFormListItem fields={fields} add={add} value={{}} />
          {(fields || []).map(({ key, name }, index) => (
            <HsInfoItem
              key={key}
              fields={fields}
              add={add}
              remove={remove}
              move={move}
              index={index}
            />
          ))}
        </>
      )}
    </FormList>
  </div>
);

function DGYesNo({ name, schemaName, labelId, ...rest }) {
  const { styles } = useNewShipmentServiceInformationFormStyles();
  const { validation } = useDynamicFormSchemaFieldDefinition({ schemaName });
  const rules = useDynamicFormValidationRules({ schemaName, validation });

  return (
    <div className={styles.newShipmentDGYesNo} {...rest}>
      <div>
        <FormattedMessage id={labelId} />
        {validation?.rules?.required && ' *'}
      </div>
      <FormItemYesNo
        name={name}
        className="size-lg text-uppercase"
        rules={rules}
      />
    </div>
  );
}

const UNNumberSearch = ({ inputClassName, index }) => (
  <FormItemSchemaDefinedSelect
    className={inputClassName}
    name={index}
    labelId="book.newShipment.label.unNumber"
    schemaName="serviceInformation.unNumber"
    formItemComponentProps={{
      formatLabel: x => (x?.code ? `${x?.code} - ${x?.text}` : x?.text),
    }}
  />
);
function UNDescriptionPreview({ index, namePrefix }) {
  const { styles } = useNewShipmentServiceInformationFormStyles();
  const { values } = useFormContext();
  const unNumber = namePrefix
    ? values?.[namePrefix]?.unNumber[index]
    : values?.unNumber[index];
  return (
    <div className={classNames(styles.newShipmentUNDescription, 'flex-start')}>
      <div className="text">{formatCodeValue(unNumber)}</div>
    </div>
  );
}
function UNNumberRow({ fields, add, remove, move, index, isLast, namePrefix }) {
  const { styles } = useNewShipmentServiceInformationFormStyles();
  const { addAfter, removeThis } = useFormListCallbacks({
    fields,
    index,
    add,
    remove,
    move,
  });

  const dividerCss = isLast ? '' : 'absolute-full';
  return (
    <Col span={24}>
      <Row className={styles.newShipmentDGStepperRow}>
        <Col span={12} className="col-1">
          <FlexCol
            style={{ height: cssVariables['input-height-md'] }}
            justify="space-between"
          >
            <IconButton
              icon="times-circle"
              type={FATypes.SOLID}
              className={styles.dangerousGoodsIcon}
              onClick={removeThis}
              data-subject="device-addons"
              data-action="delete"
            />
            <IconButton
              icon="plus-square"
              type={FATypes.SOLID}
              className={styles.dangerousGoodsIcon}
              onClick={addAfter}
              data-subject="device-addons"
              data-action="add-after"
            />
          </FlexCol>
          <UNNumberSearch index={index} />
        </Col>
        <DividerCol className={dividerCss} />
        <Col span={12} className="col-2">
          <UNDescriptionPreview index={index} namePrefix={namePrefix} />
        </Col>
      </Row>
    </Col>
  );
}

function dangerousGoodsList(fields, { add, remove, move }, namePrefix) {
  return (
    <div>
      <EnsureInitialFormListItem fields={fields || []} add={add} value={{}} />
      <Col span={24}>
        <Row>
          <Col offset={12} span={12}>
            <div className="label">
              <FormattedMessage id="book.newShipment.label.unDescription" />
            </div>
          </Col>
        </Row>
      </Col>
      <div className="spaces-vert-norm">
        {(fields || []).map(({ name, key }, index) => (
          <UNNumberRow
            fields={fields}
            key={key}
            name={name}
            add={add}
            remove={remove}
            move={move}
            index={index}
            isLast={index === (fields || []).length - 1}
            namePrefix={namePrefix}
          />
        ))}
      </div>
    </div>
  );
}

export function DangerousGoodsSection({ noDivider, namePrefix }) {
  const { styles } = useNewShipmentServiceInformationFormStyles();
  const { values } = useFormContext();
  const { dangerousGoods } = useNewOrderFeatures();
  const media = useResponsiveQueries();

  const formItemsReg = (
    <>
      <FormItemFieldRegistration
        name={mergeFormNames(namePrefix, 'dangerousGoods')}
      />
      <FormItemFieldRegistration
        name={mergeFormNames(namePrefix, 'exceptedQuantity')}
      />
      <FormItemFieldRegistration
        name={mergeFormNames(namePrefix, 'unNumber')}
      />
    </>
  );

  if (!dangerousGoods) {
    return formItemsReg;
  }

  const showStep2 = namePrefix
    ? values[namePrefix]?.dangerousGoods
    : values.dangerousGoods;

  return (
    <>
      {formItemsReg}
      {!noDivider && (
        <div className={styles.newShipmentServiceInformationSectionDivider}>
          <TwoLines className="size-10" />
          <div
            className={styles.newShipmentServiceInformationSectionDividerTitle}
          >
            <FormattedMessage id="book.newShipment.dangerousGoodsDeclaration" />
          </div>
        </div>
      )}
      <Row
        className={classNames(
          styles.newShipmentDGStepperRow,
          'no-margin-form-items'
        )}
        align="top"
        justify="space-between"
      >
        <Col span={12} className="col-1">
          <DGYesNo
            labelId="book.newShipment.label.dangerousGoods"
            name={mergeFormNames(namePrefix, 'dangerousGoods')}
            schemaName="serviceInformation.dangerousGoods"
          />
        </Col>
        {showStep2 && (
          <>
            {!media.xs && <DividerCol />}
            <Col span={12} className="col-2">
              <DGYesNo
                labelId="book.newShipment.label.exceptedQuantity"
                name={mergeFormNames(namePrefix, 'exceptedQuantity')}
                schemaName="serviceInformation.exceptedQuantity"
              />
            </Col>
          </>
        )}
        {showStep2 && (
          <>
            <Col span={24}>
              <TwoLinesSeparator />
            </Col>
            <Col span={24}>
              <FormList name={mergeFormNames(namePrefix, 'unNumber')}>
                {(fields, operations) =>
                  dangerousGoodsList(fields, operations, namePrefix)
                }
              </FormList>
            </Col>
          </>
        )}
      </Row>
    </>
  );
}

export function ServiceInformationCard({ namePrefix }) {
  const { styles } = useNewShipmentServiceInformationFormStyles();
  const { styles: newShipmentElementsStyles } = useNewShipmentElementsStyles();
  const showIncoterms = useDynamicFormSchemaFieldExists({
    schemaName: 'serviceInformation.incoterms',
  });
  const topRowSpan = showIncoterms ? 8 : 12;

  return (
    <ConfigurableCard
      titleId="book.newShipment.serviceInformation"
      bodyComponent="div"
      bodyClassName={classNames(
        'NewShipmentCardBody',
        newShipmentElementsStyles.newShipmentCardBody,
        'NewShipment-ServiceInformationCard',
        styles.newShipmentServiceInformationCard
      )}
    >
      <NewShipmentBaseRow>
        <TopRowCol span={topRowSpan}>
          <ServiceTypeField namePrefix={namePrefix} />
        </TopRowCol>
        <TopRowCol span={topRowSpan}>
          <FormItemSchemaDefinedSelect
            name={mergeFormNames(namePrefix, 'commodity')}
            schemaName="serviceInformation.commodity"
            labelId="book.newShipment.label.commodity"
            allowFreeText
            textAsValue
          />
        </TopRowCol>
        {showIncoterms && (
          <TopRowCol span={topRowSpan}>
            <IncotermsField namePrefix={namePrefix} />
          </TopRowCol>
        )}
      </NewShipmentBaseRow>
      <DangerousGoodsSection />
    </ConfigurableCard>
  );
}

export function ReferenceInformationCard({
  showJobSpecificFields = true,
  namePrefix,
}) {
  const { styles: newShipmentElementsStyles } = useNewShipmentElementsStyles();
  const { data, loading } = useQuery(USER_QUERY);
  const user = data && extractGraphqlEntity(data);

  return loading ? (
    <Spinner />
  ) : (
    <ConfigurableCard
      titleId="book.newShipment.referenceInformation"
      bodyComponent="div"
      bodyClassName={classNames(
        'NewShipmentCardBody',
        newShipmentElementsStyles.newShipmentCardBody
      )}
    >
      <NewShipmentBaseRow>
        {showJobSpecificFields && (
          <Col span={12}>
            <BolNumber namePrefix={namePrefix} />
          </Col>
        )}
        <Col span={12}>
          <CurrencyInput namePrefix={namePrefix} />
        </Col>
        <Col span={12}>
          <DeclaredValue namePrefix={namePrefix} />
        </Col>
        <Col span={12}>
          <CostCenter namePrefix={namePrefix} />
        </Col>
        <Col span={12}>
          <AdditionalInsurance namePrefix={namePrefix} />
        </Col>
        {showJobSpecificFields && (
          <Col span={12}>
            <EoriNumber namePrefix={namePrefix} />
          </Col>
        )}
        <Col span={24}>
          <ReferenceNumbers namePrefix={namePrefix} />
        </Col>
        {showJobSpecificFields && (
          <Col span={24}>
            <HsInfo namePrefix={namePrefix} />
          </Col>
        )}
        {showJobSpecificFields && user.emailNotificationsEnabled && (
          <Col span={24}>
            <NewShipmentEmailNotificationsButton />
          </Col>
        )}
      </NewShipmentBaseRow>
    </ConfigurableCard>
  );
}

export default function NewShipmentServiceInformationForm({ form, ...rest }) {
  return (
    <NewShipmentForm
      {...rest}
      form={form}
      name="serviceInformation"
      className="margins-bottom-norm2"
    >
      <ServiceInformationCard />
      <ReferenceInformationCard />
      <ShipperSpecificCard form={form} />
    </NewShipmentForm>
  );
}
